import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { LayoutComponent } from "../../shared/layout/layout.component";
import { TitleComponent } from "../../shared/title/title.component";
import { SpinnerComponent } from "../../shared/icons/spinner/spinner.component";
import { InvitationService } from "../../services/invitation.service";
import { InviteSessionService, forwardedInviteQueryParams } from "../../services/invite-session.service";
import { Version, VersionService } from "../../services/version.service";

/**
 * Landing page of the invitation link (docs/invite-flow.md). Validates the token with the
 * IPA Login UI API and routes on: valid -> /invite/set-password, expired or broken link ->
 * /invite/expired. Only "the API is unreachable" is shown here.
 */
@Component({
  selector: "app-invite",
  standalone: true,
  imports: [TranslatePipe, LayoutComponent, TitleComponent, SpinnerComponent],
  templateUrl: "./invite.component.html",
  styleUrl: "./invite.component.css",
})
export class InviteComponent implements OnInit {
  unavailable = false;
  layoutType: "blue" | "yellow";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invitationService: InvitationService,
    private inviteSession: InviteSessionService,
    private versionService: VersionService,
  ) {
    this.layoutType = this.versionService.current === Version.B2B ? "blue" : "yellow";
  }

  async ngOnInit(): Promise<void> {
    const token = this.readToken();
    this.removeTokenFromUrl();

    if (!token) {
      console.warn("InviteComponent.ngOnInit - no invitation token in the URL.");
      this.inviteSession.clear();
      this.go("/invite/expired");
      return;
    }

    this.inviteSession.store(token);
    const origin = this.versionService.current === Version.B2B ? "b2b" : "b2c";
    const outcome = await this.invitationService.validateInvite(token, origin);

    switch (outcome.kind) {
      case "valid":
        this.go("/invite/set-password");
        return;
      case "expired":
      case "invalidLink":
        this.inviteSession.clear();
        this.go("/invite/expired");
        return;
      default:
        this.unavailable = true;
    }
  }

  private go(path: string): void {
    this.router.navigate([path], { queryParams: forwardedInviteQueryParams(), replaceUrl: true });
  }

  /** Token from the fragment (#token=, preferred: never sent to a server), the query or the path. */
  private readToken(): string | null {
    const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token");
    const fromQuery = new URLSearchParams(window.location.search).get("token");
    const fromPath = this.route.snapshot.paramMap.get("token");
    return fromHash || fromQuery || fromPath || null;
  }

  /** Keeps the token out of history, bookmarks, analytics and the Referer of later requests. */
  private removeTokenFromUrl(): void {
    const query = new URLSearchParams(forwardedInviteQueryParams()).toString();
    window.history.replaceState(window.history.state, "", `/invite${query ? "?" + query : ""}`);
  }
}
